import React, { useState, useEffect } from 'react';
import { Layout } from '@layout/Layout';
import { Settings, Calendar, ArrowRightLeft, Calculator, Save, Play, ChevronDown, Search, AlertTriangle, X, CheckCircle, Info } from 'lucide-react';
import { useTheme } from '@contexts/ThemeContext';
import useLeavePolicyStore from '@/store/LeavePolicyStore';
import './LeavePolicySettings.css';

const LeavePolicySettings: React.FC = () => {
    const { isDarkMode } = useTheme();
    const {
        holidayConfig,
        carryForwardRules,
        carryForwardLog,
        proRataSettings,
        availableFinancialYears,
        yearlyQuotas,
        error,
        activeTab,
        fetchAllSettings,
        fetchHolidayConfig,
        updateHolidayConfig,
        updateCarryForwardRule,
        triggerCarryForward,
        fetchCarryForwardLog,
        updateProRataSettings,
        fetchAvailableFinancialYears,
        initializeFinancialYear,
        fetchYearlyQuotas,
        updateYearlyQuota,
        setActiveTab,
    } = useLeavePolicyStore();

    // Holiday config local state
    const [maxOptional, setMaxOptional] = useState(4);
    const [holidaySaving, setHolidaySaving] = useState(false);
    const [holidaySuccess, setHolidaySuccess] = useState('');
    const [holidayConfigYear, setHolidayConfigYear] = useState<number>(() => {
        const now = new Date();
        return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    });
    const [isGlobalConfig, setIsGlobalConfig] = useState(true);

    // Carry forward local state
    const [cfSaving, setCfSaving] = useState<number | null>(null);
    const [cfSuccess, setCfSuccess] = useState('');
    const [cfProcessYear, setCfProcessYear] = useState<number>(new Date().getFullYear() - 1);
    const [cfProcessing, setCfProcessing] = useState(false);
    const [showCfConfirm, setShowCfConfirm] = useState(false);
    const [showCfLog, setShowCfLog] = useState(false);

    // Initialize FY state
    const [initFyYear, setInitFyYear] = useState<string>(() => {
        const now = new Date();
        const startYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
        return `${startYear}-${startYear + 1}`;
    });
    const [initFyProcessing, setInitFyProcessing] = useState(false);
    const [showInitFyConfirm, setShowInitFyConfirm] = useState(false);

    // Year-specific quota state
    const [showQuotaEditor, setShowQuotaEditor] = useState(false);
    const [quotaSaving, setQuotaSaving] = useState<number | null>(null);
    const [quotaSuccess, setQuotaSuccess] = useState('');
    const [editedQuotas, setEditedQuotas] = useState<Record<number, number>>({});

    // Modal state for error/warning messages
    const [showModal, setShowModal] = useState(false);
    const [modalContent, setModalContent] = useState<{
        type: 'error' | 'warning' | 'success' | 'info';
        title: string;
        message: string;
    } | null>(null);

    // Carry forward log pagination and search
    const [cfLogSearchQuery, setCfLogSearchQuery] = useState('');
    const [cfLogCurrentPage, setCfLogCurrentPage] = useState(1);
    const cfLogItemsPerPage = 10;

    // Pro-rata local state
    const [proRataEnabled, setProRataEnabled] = useState(true);
    const [roundingMethod, setRoundingMethod] = useState<'round' | 'ceil' | 'floor'>('round');
    const [proRataSaving, setProRataSaving] = useState(false);
    const [proRataSuccess, setProRataSuccess] = useState('');
    const [previewDate, setPreviewDate] = useState('');
    const [previewResults, setPreviewResults] = useState<any[]>([]);

    useEffect(() => {
        fetchAllSettings();
        fetchAvailableFinancialYears();
    }, []);

    useEffect(() => {
        if (holidayConfig) {
            setMaxOptional(holidayConfig.max_optional_holidays);
        }
    }, [holidayConfig]);

    // Fetch config when year selection changes
    useEffect(() => {
        const targetYear = isGlobalConfig ? 0 : holidayConfigYear;
        fetchHolidayConfig(targetYear);
    }, [isGlobalConfig, holidayConfigYear]);

    useEffect(() => {
        if (proRataSettings) {
            setProRataEnabled(proRataSettings.enabled);
            setRoundingMethod(proRataSettings.rounding_method);
        }
    }, [proRataSettings]);

    // Set default carry forward year to the latest available year
    useEffect(() => {
        if (availableFinancialYears.length > 0) {
            // availableFinancialYears is sorted DESC, so first one is the latest
            const latestYear = parseInt(availableFinancialYears[0].split('-')[0]);
            setCfProcessYear(latestYear);
        }
    }, [availableFinancialYears]);

    // Fetch yearly quotas when showQuotaEditor changes or year changes
    useEffect(() => {
        if (showQuotaEditor && cfProcessYear) {
            const toFyYear = `${cfProcessYear + 1}-${cfProcessYear + 2}`;
            fetchYearlyQuotas(toFyYear);
        }
    }, [showQuotaEditor, cfProcessYear]);

    // ─── Holiday Config Handlers ─────────────────────────────────────────────

    const handleSaveHolidayConfig = async () => {
        setHolidaySaving(true);
        setHolidaySuccess('');
        const targetYear = isGlobalConfig ? 0 : holidayConfigYear;
        const result = await updateHolidayConfig({
            max_optional_holidays: maxOptional,
            fy_year: targetYear
        });
        setHolidaySaving(false);
        if (result.success) {
            setHolidaySuccess('Holiday configuration saved successfully');
            setTimeout(() => setHolidaySuccess(''), 3000);
        }
    };

    // ─── Carry Forward Handlers ──────────────────────────────────────────────

    const handleSaveCfRule = async (rule: any) => {
        setCfSaving(rule.id);
        setCfSuccess('');
        const result = await updateCarryForwardRule(rule.id, {
            carry_forward_enabled: rule.carry_forward_enabled,
            max_carry_forward_days: rule.max_carry_forward_days,
            carry_forward_expiry_months: rule.carry_forward_expiry_months,
        });
        setCfSaving(null);
        if (result.success) {
            setCfSuccess(`${rule.leave_name} carry-forward rule updated`);
            setTimeout(() => setCfSuccess(''), 3000);
        }
    };

    const handleInitializeFinancialYear = async () => {
        setInitFyProcessing(true);
        const result = await initializeFinancialYear(initFyYear);
        setInitFyProcessing(false);
        setShowInitFyConfirm(false);

        if (result.success) {
            setModalContent({
                type: 'success',
                title: 'FY Balances Initialized',
                message: `FY ${initFyYear}: ${result.data?.initialized_count || 0} balance records created, ${result.data?.skipped_count || 0} already existed.`
            });
        } else {
            setModalContent({
                type: 'error',
                title: 'Initialization Failed',
                message: result.error || 'Failed to initialize financial year balances.'
            });
        }
        setShowModal(true);
    };

    const handleProcessCarryForward = async () => {
        setCfProcessing(true);
        const result = await triggerCarryForward(cfProcessYear);
        setCfProcessing(false);
        setShowCfConfirm(false);

        if (result.success) {
            setModalContent({
                type: 'success',
                title: 'Success',
                message: `Carry forward processed successfully from FY ${result.data.from_fy} to ${result.data.to_fy}. ${result.data.processed_users} user-leave combinations processed.`
            });
            setShowModal(true);
        } else {
            // Handle different error types
            const errorMessage = result.error?.message || 'Failed to process carry forward';
            const statusCode = result.error?.statusCode;

            let modalType: 'error' | 'warning' = 'error';
            let modalTitle = 'Error';

            // Check for specific error codes or keywords for warnings
            if (statusCode === 409 || errorMessage.includes('already been processed')) {
                modalType = 'warning';
                modalTitle = 'Already Processed';
            } else if (errorMessage.includes('future year') || errorMessage.includes('current year')) {
                modalType = 'warning';
                modalTitle = 'Invalid Year';
            } else if (errorMessage.includes("hasn't ended yet")) {
                modalType = 'warning';
                modalTitle = 'Year Not Complete';
            }

            setModalContent({
                type: modalType,
                title: modalTitle,
                message: errorMessage
            });
            setShowModal(true);
        }

        // Refresh carry forward log after processing (success or failure)
        await fetchCarryForwardLog();
    };

    const handleShowLog = async () => {
        setShowCfLog(!showCfLog);
        if (!showCfLog) {
            await fetchCarryForwardLog();
        }
    };

    // ─── Year-Specific Quota Handlers ────────────────────────────────────────

    const handleToggleQuotaEditor = () => {
        setShowQuotaEditor(!showQuotaEditor);
        setEditedQuotas({});
        setQuotaSuccess('');
    };

    const handleQuotaChange = (leaveTypeId: number, newQuota: number) => {
        setEditedQuotas(prev => ({
            ...prev,
            [leaveTypeId]: newQuota
        }));
    };

    const handleSaveQuota = async (leaveTypeId: number) => {
        const toFyYear = `${cfProcessYear + 1}-${cfProcessYear + 2}`;
        const newQuota = editedQuotas[leaveTypeId];

        if (newQuota === undefined) return;

        setQuotaSaving(leaveTypeId);
        setQuotaSuccess('');

        const result = await updateYearlyQuota(leaveTypeId, toFyYear, newQuota);

        setQuotaSaving(null);

        if (result.success) {
            setQuotaSuccess('Quota updated successfully');
            setEditedQuotas(prev => {
                const newEdited = { ...prev };
                delete newEdited[leaveTypeId];
                return newEdited;
            });
            setTimeout(() => setQuotaSuccess(''), 3000);
        }
    };

    // Format date to user-friendly format (e.g., "31 Jul 2026")
    const formatExpiryDate = (dateStr: string | null) => {
        if (!dateStr) return 'No expiry';
        const date = new Date(dateStr);
        const day = date.getDate();
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    // Filter and paginate carry forward log
    const filteredCfLog = carryForwardLog.filter((entry) => {
        if (!cfLogSearchQuery) return true;
        const query = cfLogSearchQuery.toLowerCase();
        return (
            entry.employee_name?.toLowerCase().includes(query) ||
            entry.leave_name?.toLowerCase().includes(query) ||
            entry.status?.toLowerCase().includes(query)
        );
    });

    const totalCfLogPages = Math.ceil(filteredCfLog.length / cfLogItemsPerPage);
    const paginatedCfLog = filteredCfLog.slice(
        (cfLogCurrentPage - 1) * cfLogItemsPerPage,
        cfLogCurrentPage * cfLogItemsPerPage
    );

    // Reset to page 1 when search query changes
    useEffect(() => {
        setCfLogCurrentPage(1);
    }, [cfLogSearchQuery]);

    // ─── Pro-Rata Handlers ───────────────────────────────────────────────────

    const handleSaveProRata = async () => {
        setProRataSaving(true);
        setProRataSuccess('');
        const result = await updateProRataSettings({
            enabled: proRataEnabled,
            rounding_method: roundingMethod
        });
        setProRataSaving(false);
        if (result.success) {
            setProRataSuccess('Pro-rata settings saved successfully');
            setTimeout(() => setProRataSuccess(''), 3000);
        }
    };

    const handlePreviewProRata = () => {
        if (!previewDate || !carryForwardRules.length) return;
        const hire = new Date(previewDate);
        const fyStartYear = hire.getMonth() >= 3 ? hire.getFullYear() : hire.getFullYear() - 1;
        const fyStart = new Date(`${fyStartYear}-04-01`);
        const fyEnd = new Date(`${fyStartYear + 1}-03-31`);

        const results = carryForwardRules
            .filter(r => r.is_pro_rata_applicable)
            .map(r => {
                let amount: number;
                if (hire <= fyStart) {
                    amount = r.total_leaves;
                } else if (hire > fyEnd) {
                    amount = 0;
                } else {
                    const remainingMonths = (fyEnd.getFullYear() - hire.getFullYear()) * 12 + (fyEnd.getMonth() - hire.getMonth()) + 1;
                    const raw = (r.total_leaves / 12) * remainingMonths;
                    if (roundingMethod === 'ceil') amount = Math.ceil(raw);
                    else if (roundingMethod === 'floor') amount = Math.floor(raw);
                    else amount = Math.round(raw);
                }
                return { leave_name: r.leave_name, total: r.total_leaves, pro_rata: amount };
            });
        setPreviewResults(results);
    };

    const tabs = [
        { label: 'Holiday Config', icon: <Calendar size={16} /> },
        { label: 'Carry Forward', icon: <ArrowRightLeft size={16} /> },
        { label: 'Pro-Rata', icon: <Calculator size={16} /> },
    ];

    return (
        <Layout>
            <div className="leave-policy-container" data-theme={isDarkMode ? 'dark' : 'light'}>
                {/* Header */}
                <div className="leave-policy-header">
                    <div className="leave-policy-header-icon">
                        <Settings size={28} />
                    </div>
                    <div>
                        <h1 className="leave-policy-title">Leave Policy Settings</h1>
                        <p className="leave-policy-subtitle">Configure holiday limits, carry-forward rules, and pro-rata calculations</p>
                    </div>
                </div>

                {/* Tabs */}
                <div className="leave-policy-tabs">
                    {tabs.map((tab, index) => (
                        <button
                            key={index}
                            onClick={() => setActiveTab(index)}
                            className={`leave-policy-tab ${activeTab === index ? 'active' : ''}`}
                        >
                            {tab.icon}
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* ── Tab 0: Holiday Config ── */}
                {activeTab === 0 && (
                    <div className="leave-policy-card">
                        <h2 className="leave-policy-section-title">Holiday Configuration</h2>
                        <p className="leave-policy-section-description">
                            Set the maximum number of optional holidays employees can select per financial year.
                        </p>

                        {/* Year Selection */}
                        <div style={{ maxWidth: 600, marginBottom: 24 }}>
                            <div className="leave-policy-form-group">
                                <label className="leave-policy-label">Configuration Scope</label>
                                <select
                                    value={isGlobalConfig ? 'global' : 'year-specific'}
                                    onChange={(e) => setIsGlobalConfig(e.target.value === 'global')}
                                    className="leave-policy-select"
                                >
                                    <option value="global">Global (applies to all years)</option>
                                    <option value="year-specific">Year-Specific</option>
                                </select>
                            </div>

                            {!isGlobalConfig && (
                                <div className="leave-policy-form-group">
                                    <label className="leave-policy-label">Financial Year</label>
                                    <select
                                        value={holidayConfigYear}
                                        onChange={(e) => setHolidayConfigYear(parseInt(e.target.value))}
                                        className="leave-policy-select"
                                    >
                                        {Array.from({ length: 5 }, (_, i) => {
                                            const year = new Date().getFullYear() - 1 + i;
                                            return <option key={year} value={year}>FY {year}-{year + 1}</option>;
                                        })}
                                    </select>
                                </div>
                            )}
                        </div>

                        {/* Configuration Value */}
                        <div className="leave-policy-form-group" style={{ maxWidth: 400 }}>
                            <label className="leave-policy-label">Max Optional Holidays (per employee)</label>
                            <input
                                type="number"
                                min={1}
                                max={20}
                                value={maxOptional}
                                onChange={(e) => setMaxOptional(parseInt(e.target.value) || 1)}
                                className="leave-policy-input"
                            />
                            <p className="leave-policy-hint">Currently: {holidayConfig?.max_optional_holidays || 4}</p>
                        </div>

                        <div className="leave-policy-btn-row">
                            <button
                                onClick={handleSaveHolidayConfig}
                                disabled={holidaySaving}
                                className="leave-policy-btn primary"
                            >
                                <Save size={16} />
                                {holidaySaving ? 'Saving...' : 'Save Configuration'}
                            </button>
                            {holidaySuccess && <span className="leave-policy-success-text">{holidaySuccess}</span>}
                        </div>
                    </div>
                )}

                {/* ── Tab 1: Carry Forward ── */}
                {activeTab === 1 && (
                    <div className="leave-policy-card-stack">
                        {/* Carry Forward Rules */}
                        <div className="leave-policy-card">
                            <h2 className="leave-policy-section-title">Carry Forward Rules</h2>
                            <p className="leave-policy-section-description">
                                Configure which leave types allow carrying forward unused days to the next financial year.
                            </p>

                            {cfSuccess && <div className="leave-policy-alert success">{cfSuccess}</div>}

                            <div className="leave-policy-table-wrapper">
                                <table className="leave-policy-table">
                                    <thead>
                                        <tr>
                                            <th>Leave Type</th>
                                            <th>Annual Quota</th>
                                            <th className="center">Enabled</th>
                                            <th>Max Days</th>
                                            <th>Expiry (months)</th>
                                            <th className="center">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {carryForwardRules.map((rule) => (
                                            <CarryForwardRow
                                                key={rule.id}
                                                rule={rule}
                                                saving={cfSaving === rule.id}
                                                onSave={handleSaveCfRule}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Initialize FY Balances */}
                        <div className="leave-policy-card">
                            <h2 className="leave-policy-section-title">Initialize FY Balances</h2>
                            <p className="leave-policy-section-description">
                                Create leave balance records for all active employees for a financial year. Records that already exist will not be modified.
                                Run this before processing carry-forward for a new year.
                            </p>

                            <div className="leave-policy-process-row">
                                <div className="leave-policy-form-group">
                                    <label className="leave-policy-label">Financial Year</label>
                                    <select
                                        value={initFyYear}
                                        onChange={(e) => setInitFyYear(e.target.value)}
                                        className="leave-policy-select"
                                    >
                                        {availableFinancialYears.map((fyYear: string) => (
                                            <option key={fyYear} value={fyYear}>{fyYear}</option>
                                        ))}
                                    </select>
                                </div>
                                <button
                                    onClick={() => setShowInitFyConfirm(true)}
                                    disabled={initFyProcessing}
                                    className="leave-policy-btn primary"
                                >
                                    <Play size={16} />
                                    {initFyProcessing ? 'Initializing...' : 'Initialize Balances'}
                                </button>
                            </div>

                            {showInitFyConfirm && (
                                <div className="leave-policy-confirm-box">
                                    <p className="confirm-title">
                                        Initialize leave balances for FY {initFyYear}?
                                    </p>
                                    <p className="confirm-subtitle">This will create leave balance records for all active employees. Existing records will not be overwritten.</p>
                                    <div className="confirm-actions">
                                        <button onClick={handleInitializeFinancialYear} disabled={initFyProcessing} className="leave-policy-btn primary small">
                                            {initFyProcessing ? 'Initializing...' : 'Confirm'}
                                        </button>
                                        <button onClick={() => setShowInitFyConfirm(false)} className="leave-policy-btn secondary small">Cancel</button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Process Carry Forward */}
                        <div className="leave-policy-card">
                            <h2 className="leave-policy-section-title">Process Carry Forward</h2>
                            <p className="leave-policy-section-description">
                                Trigger carry-forward processing for a financial year transition. This calculates remaining balances and credits them to the next FY.
                            </p>

                            {/* Year-Specific Quota Editor */}
                            <div style={{ marginBottom: '24px', padding: '16px', background: 'var(--card-bg)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                                    <div>
                                        <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0, marginBottom: '4px' }}>Annual Leave Quotas (Optional)</h3>
                                        <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: 0 }}>
                                            Set year-specific quotas for FY {cfProcessYear + 1}-{cfProcessYear + 2}. Leave blank to use default quotas.
                                        </p>
                                    </div>
                                    <button
                                        onClick={handleToggleQuotaEditor}
                                        className="leave-policy-btn secondary small"
                                    >
                                        <ChevronDown size={16} style={{ transform: showQuotaEditor ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                                        {showQuotaEditor ? 'Hide' : 'Edit Quotas'}
                                    </button>
                                </div>

                                {showQuotaEditor && (
                                    <>
                                        {quotaSuccess && <div className="leave-policy-alert success" style={{ marginTop: '12px' }}>{quotaSuccess}</div>}

                                        <div className="leave-policy-table-wrapper" style={{ marginTop: '16px' }}>
                                            <table className="leave-policy-table">
                                                <thead>
                                                    <tr>
                                                        <th>Leave Type</th>
                                                        <th>Default Quota</th>
                                                        <th>FY {cfProcessYear + 1}-{cfProcessYear + 2} Quota</th>
                                                        <th className="center">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {yearlyQuotas.map((quota) => {
                                                        const currentValue = editedQuotas[quota.leave_type_id] ?? quota.effective_quota;
                                                        const hasChanged = editedQuotas[quota.leave_type_id] !== undefined;

                                                        return (
                                                            <tr key={quota.leave_type_id}>
                                                                <td className="bold">{quota.leave_name}</td>
                                                                <td className="secondary">{quota.default_quota} days</td>
                                                                <td>
                                                                    <input
                                                                        type="number"
                                                                        min={0}
                                                                        value={currentValue}
                                                                        onChange={(e) => handleQuotaChange(quota.leave_type_id, parseInt(e.target.value) || 0)}
                                                                        className="leave-policy-input small"
                                                                        style={{ maxWidth: '120px' }}
                                                                    />
                                                                    {quota.yearly_quota !== null && !hasChanged && (
                                                                        <span className="leave-policy-hint" style={{ marginLeft: '8px', fontSize: '12px' }}>
                                                                            (custom)
                                                                        </span>
                                                                    )}
                                                                </td>
                                                                <td className="center">
                                                                    <button
                                                                        onClick={() => handleSaveQuota(quota.leave_type_id)}
                                                                        disabled={!hasChanged || quotaSaving === quota.leave_type_id}
                                                                        className="leave-policy-btn primary small"
                                                                    >
                                                                        {quotaSaving === quota.leave_type_id ? '...' : 'Save'}
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>

                                        <p className="leave-policy-hint" style={{ marginTop: '12px', fontSize: '13px' }}>
                                            💡 Tip: These quotas will only apply to FY {cfProcessYear + 1}-{cfProcessYear + 2}. Other years will use default quotas.
                                        </p>
                                    </>
                                )}
                            </div>

                            <div className="leave-policy-process-row">
                                <div className="leave-policy-form-group">
                                    <label className="leave-policy-label">From Financial Year</label>
                                    <select
                                        value={cfProcessYear}
                                        onChange={(e) => setCfProcessYear(parseInt(e.target.value))}
                                        className="leave-policy-select"
                                    >
                                        {availableFinancialYears.map((fyYear) => {
                                            const year = parseInt(fyYear.split('-')[0]);
                                            return <option key={fyYear} value={year}>{fyYear}</option>;
                                        })}
                                    </select>
                                </div>
                                <button
                                    onClick={() => setShowCfConfirm(true)}
                                    disabled={cfProcessing}
                                    className="leave-policy-btn warning"
                                >
                                    <Play size={16} />
                                    {cfProcessing ? 'Processing...' : 'Process Carry Forward'}
                                </button>
                                <button
                                    onClick={handleShowLog}
                                    className="leave-policy-btn secondary"
                                >
                                    <ChevronDown size={16} style={{ transform: showCfLog ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                                    {showCfLog ? 'Hide Log' : 'View Log'}
                                </button>
                            </div>

                            {/* Confirmation Dialog */}
                            {showCfConfirm && (
                                <div className="leave-policy-confirm-box">
                                    <p className="confirm-title">
                                        Are you sure you want to process carry forward from FY {cfProcessYear}-{cfProcessYear + 1} to FY {cfProcessYear + 1}-{cfProcessYear + 2}?
                                    </p>
                                    <p className="confirm-subtitle">This will calculate remaining balances and credit carry-forward amounts for all eligible employees.</p>
                                    <div className="confirm-actions">
                                        <button onClick={handleProcessCarryForward} disabled={cfProcessing} className="leave-policy-btn warning small">
                                            {cfProcessing ? 'Processing...' : 'Confirm'}
                                        </button>
                                        <button onClick={() => setShowCfConfirm(false)} className="leave-policy-btn secondary small">Cancel</button>
                                    </div>
                                </div>
                            )}

                            {/* Carry Forward Log */}
                            {showCfLog && (
                                <div className="leave-policy-log-section">
                                    {/* Search Bar */}
                                    <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', maxWidth: '400px' }}>
                                        <Search size={18} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                                        <input
                                            type="text"
                                            placeholder="Search by employee, leave type, or status..."
                                            value={cfLogSearchQuery}
                                            onChange={(e) => setCfLogSearchQuery(e.target.value)}
                                            className="leave-policy-input"
                                            style={{ margin: 0 }}
                                        />
                                        {cfLogSearchQuery && (
                                            <button
                                                onClick={() => setCfLogSearchQuery('')}
                                                className="leave-policy-btn secondary small"
                                                style={{ whiteSpace: 'nowrap' }}
                                            >
                                                Clear
                                            </button>
                                        )}
                                    </div>

                                    {/* Table with horizontal scroll */}
                                    <div style={{ overflowX: 'auto', width: '100%' }}>
                                        <table className="leave-policy-table">
                                            <thead>
                                                <tr>
                                                    <th>Employee</th>
                                                    <th>Leave Type</th>
                                                    <th>From FY</th>
                                                    <th className="right">Remaining</th>
                                                    <th className="right">Carried</th>
                                                    <th>Expiry</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paginatedCfLog.length === 0 ? (
                                                    <tr><td colSpan={7} className="center secondary" style={{ padding: '16px' }}>
                                                        {cfLogSearchQuery ? 'No matching entries found' : 'No carry-forward log entries'}
                                                    </td></tr>
                                                ) : paginatedCfLog.map((entry) => (
                                                    <tr key={entry.id}>
                                                        <td>{entry.employee_name}</td>
                                                        <td>{entry.leave_name}</td>
                                                        <td className="secondary">FY {entry.from_fy_year}-{entry.from_fy_year + 1}</td>
                                                        <td className="right secondary">{entry.remaining_leaves}</td>
                                                        <td className="right bold">{entry.carried_forward_amount}</td>
                                                        <td className="secondary">{formatExpiryDate(entry.expiry_date)}</td>
                                                        <td>
                                                            <span className={`leave-policy-status-badge ${entry.status}`}>
                                                                {entry.status}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination Controls */}
                                    {filteredCfLog.length > 0 && (
                                        <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                                            <div className="secondary" style={{ fontSize: '14px' }}>
                                                Showing {((cfLogCurrentPage - 1) * cfLogItemsPerPage) + 1} to {Math.min(cfLogCurrentPage * cfLogItemsPerPage, filteredCfLog.length)} of {filteredCfLog.length} entries
                                                {cfLogSearchQuery && ` (filtered from ${carryForwardLog.length} total)`}
                                            </div>
                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                <button
                                                    onClick={() => setCfLogCurrentPage(cfLogCurrentPage - 1)}
                                                    disabled={cfLogCurrentPage === 1}
                                                    className="leave-policy-btn secondary small"
                                                >
                                                    Previous
                                                </button>
                                                <span className="secondary" style={{ fontSize: '14px' }}>
                                                    Page {cfLogCurrentPage} of {totalCfLogPages}
                                                </span>
                                                <button
                                                    onClick={() => setCfLogCurrentPage(cfLogCurrentPage + 1)}
                                                    disabled={cfLogCurrentPage >= totalCfLogPages}
                                                    className="leave-policy-btn secondary small"
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ── Tab 2: Pro-Rata ── */}
                {activeTab === 2 && (
                    <div className="leave-policy-card-stack">
                        <div className="leave-policy-card">
                            <h2 className="leave-policy-section-title">Pro-Rata Leave Settings</h2>
                            <p className="leave-policy-section-description">
                                When enabled, new employees automatically receive leave balances calculated proportionally based on their date of joining.
                            </p>

                            <div style={{ maxWidth: 640 }}>
                                <div className="leave-policy-setting-row">
                                    <div className="leave-policy-setting-info">
                                        <div className="setting-label">Enable Pro-Rata Calculation</div>
                                        <div className="setting-hint">Auto-credit proportional leave on employee creation</div>
                                    </div>
                                    <button
                                        onClick={() => setProRataEnabled(!proRataEnabled)}
                                        className={`leave-policy-toggle ${proRataEnabled ? 'active' : ''}`}
                                    >
                                        <span className="leave-policy-toggle-knob" />
                                    </button>
                                </div>

                                <div className="leave-policy-form-group" style={{ marginTop: 16 }}>
                                    <label className="leave-policy-label">Rounding Method</label>
                                    <select
                                        value={roundingMethod}
                                        onChange={(e) => setRoundingMethod(e.target.value as 'round' | 'ceil' | 'floor')}
                                        className="leave-policy-select"
                                        style={{ maxWidth: 280 }}
                                    >
                                        <option value="round">Round (nearest whole number)</option>
                                        <option value="ceil">Ceil (round up)</option>
                                        <option value="floor">Floor (round down)</option>
                                    </select>
                                </div>

                                <div className="leave-policy-btn-row">
                                    <button
                                        onClick={handleSaveProRata}
                                        disabled={proRataSaving}
                                        className="leave-policy-btn primary"
                                    >
                                        <Save size={16} />
                                        {proRataSaving ? 'Saving...' : 'Save Settings'}
                                    </button>
                                    {proRataSuccess && <span className="leave-policy-success-text">{proRataSuccess}</span>}
                                </div>
                            </div>
                        </div>

                        {/* Pro-Rata Applicability per Leave Type */}
                        <div className="leave-policy-card">
                            <h2 className="leave-policy-section-title">Leave Type Applicability</h2>
                            <p className="leave-policy-section-description">Choose which leave types are subject to pro-rata calculation.</p>

                            <div className="leave-policy-leave-type-list">
                                {carryForwardRules.map((rule) => (
                                    <div key={rule.id} className="leave-policy-leave-type-item">
                                        <div>
                                            <span className="type-name">{rule.leave_name}</span>
                                            <span className="type-quota">({rule.total_leaves} days/year)</span>
                                        </div>
                                        <button
                                            onClick={async () => {
                                                await updateCarryForwardRule(rule.id, {
                                                    is_pro_rata_applicable: rule.is_pro_rata_applicable ? 0 : 1
                                                } as any);
                                            }}
                                            className={`leave-policy-toggle small ${rule.is_pro_rata_applicable ? 'active' : ''}`}
                                        >
                                            <span className="leave-policy-toggle-knob" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Pro-Rata Calculator Preview */}
                        <div className="leave-policy-card">
                            <h2 className="leave-policy-section-title">Pro-Rata Calculator Preview</h2>
                            <p className="leave-policy-section-description">Enter a hire date to preview the pro-rata leave allocation.</p>

                            <div className="leave-policy-calc-row">
                                <div className="leave-policy-form-group">
                                    <label className="leave-policy-label">Hire Date</label>
                                    <input
                                        type="date"
                                        value={previewDate}
                                        onChange={(e) => setPreviewDate(e.target.value)}
                                        className="leave-policy-input"
                                    />
                                </div>
                                <button
                                    onClick={handlePreviewProRata}
                                    disabled={!previewDate}
                                    className="leave-policy-btn purple"
                                >
                                    <Calculator size={16} />
                                    Calculate
                                </button>
                            </div>

                            {previewResults.length > 0 && (
                                <div className="leave-policy-table-wrapper">
                                    <table className="leave-policy-table">
                                        <thead>
                                            <tr>
                                                <th>Leave Type</th>
                                                <th className="right">Annual Quota</th>
                                                <th className="right">Pro-Rata Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {previewResults.map((r, i) => (
                                                <tr key={i}>
                                                    <td>{r.leave_name}</td>
                                                    <td className="right secondary">{r.total}</td>
                                                    <td className="right blue">{r.pro_rata}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Message Modal */}
                {showModal && modalContent && (
                    <div className="lp-modal-overlay" onClick={() => setShowModal(false)}>
                        <div className="lp-modal-container" onClick={(e) => e.stopPropagation()}>
                            <div className="lp-modal-header">
                                <div className="lp-modal-header-content">
                                    {modalContent.type === 'error' && (
                                        <div className="lp-modal-icon lp-modal-icon-error">
                                            <AlertTriangle size={24} />
                                        </div>
                                    )}
                                    {modalContent.type === 'warning' && (
                                        <div className="lp-modal-icon lp-modal-icon-warning">
                                            <AlertTriangle size={24} />
                                        </div>
                                    )}
                                    {modalContent.type === 'success' && (
                                        <div className="lp-modal-icon lp-modal-icon-success">
                                            <CheckCircle size={24} />
                                        </div>
                                    )}
                                    {modalContent.type === 'info' && (
                                        <div className="lp-modal-icon lp-modal-icon-info">
                                            <Info size={24} />
                                        </div>
                                    )}
                                    <h3 className="lp-modal-title">{modalContent.title}</h3>
                                </div>
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="lp-modal-close-btn"
                                    aria-label="Close modal"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                            <div className="lp-modal-body">
                                <p className="lp-modal-message">{modalContent.message}</p>
                            </div>
                            <div className="lp-modal-footer">
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="leave-policy-btn secondary"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

// ─── Carry Forward Row Component ─────────────────────────────────────────────

interface CarryForwardRowProps {
    rule: any;
    saving: boolean;
    onSave: (rule: any) => void;
}

const CarryForwardRow: React.FC<CarryForwardRowProps> = ({ rule, saving, onSave }) => {
    const [enabled, setEnabled] = useState(!!rule.carry_forward_enabled);
    const [maxDays, setMaxDays] = useState(rule.max_carry_forward_days || 0);
    const [expiryMonths, setExpiryMonths] = useState<number | ''>(rule.carry_forward_expiry_months ?? '');

    useEffect(() => {
        setEnabled(!!rule.carry_forward_enabled);
        setMaxDays(rule.max_carry_forward_days || 0);
        setExpiryMonths(rule.carry_forward_expiry_months ?? '');
    }, [rule]);

    return (
        <tr>
            <td className="bold">{rule.leave_name}</td>
            <td>{rule.total_leaves}</td>
            <td className="center">
                <button
                    onClick={() => setEnabled(!enabled)}
                    className={`leave-policy-toggle small ${enabled ? 'active' : ''}`}
                >
                    <span className="leave-policy-toggle-knob" />
                </button>
            </td>
            <td>
                <input
                    type="number"
                    min={0}
                    step={0.5}
                    value={maxDays}
                    onChange={(e) => setMaxDays(parseFloat(e.target.value) || 0)}
                    disabled={!enabled}
                    className="leave-policy-input small"
                />
            </td>
            <td>
                <input
                    type="number"
                    min={0}
                    value={expiryMonths}
                    onChange={(e) => setExpiryMonths(e.target.value === '' ? '' : parseInt(e.target.value))}
                    disabled={!enabled}
                    placeholder="No expiry"
                    className="leave-policy-input medium"
                />
            </td>
            <td className="center">
                <button
                    onClick={() => onSave({
                        ...rule,
                        carry_forward_enabled: enabled ? 1 : 0,
                        max_carry_forward_days: maxDays,
                        carry_forward_expiry_months: expiryMonths === '' ? null : expiryMonths,
                    })}
                    disabled={saving}
                    className="leave-policy-btn primary small"
                >
                    {saving ? '...' : 'Save'}
                </button>
            </td>
        </tr>
    );
};

export default LeavePolicySettings;
